import React from 'react';
import { Form, Row, Col, Card, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Select from 'react-select';
import { FaSearch, FaTrash, FaInfoCircle } from 'react-icons/fa';
import { selectStylesSm } from '~/Styles/reactSelectStyles';

// W-0928 — opções fixas paridade ERP (URelContasSemSaldoConf.dfm)
const TIPO_ATIVO_OPTIONS = [
  { value: 'Moedas', label: 'Moedas' },
  { value: 'MoedasPrazo', label: 'Moedas à Prazo' },
  { value: 'Imoveis', label: 'Imóveis' },
  { value: 'Imobilizado', label: 'Imobilizado' },
];

// PRD §4.1 — domínio Conta.Tipo 1..8 (paridade ERP)
export const TIPOS_CONTA_OPTIONS = [
  { value: 1, label: 'Factoring' },
  { value: 2, label: 'Despesas' },
  { value: 3, label: 'Banco' },
  { value: 4, label: 'Fornecedor' },
  { value: 5, label: 'Comércio' },
  { value: 6, label: 'Interna' },
  { value: 7, label: 'Receita' },
  { value: 8, label: 'Prestador de serviço' },
];

const AGRUPAMENTO_OPTIONS = [
  { value: 'UsuarioResponsavel', label: 'Usuário Responsável' },
  { value: 'GrupoEmpresaConta', label: 'GE / Conta' },
  { value: 'Vendedor', label: 'Vendedor' },
];

const TOOLTIP_CHECKBOX14 =
  'Exclui contas cujo saldo foi conferido hoje (paridade ERP — comportamento legado preservado).';

const toGeOptions = (gruposEmpresa) =>
  (gruposEmpresa || []).map((ge) => ({
    value: ge.codigo_grupo_empresa,
    label: `${ge.codigo_grupo_empresa} - ${ge.apelido || ge.descricao || ''}`,
  }));

const toGrupoGeOptions = (gruposGE) =>
  (gruposGE || []).map((g) => ({
    value: g.codigo,
    label: `${g.codigo} - ${g.descricao || ''}`,
  }));

const CardFiltros = ({
  values,
  errors,
  touched,
  setFieldValue,
  isSubmitting,
  submitForm,
  resetForm,
  defaultValues,
  gruposEmpresa,
  gruposGE,
  loading,
  loadingOpcoes,
}) => {
  const tiposConta = values.tiposConta || [];

  const todosMarcados = tiposConta.length === TIPOS_CONTA_OPTIONS.length;

  const toggleTodos = (marcar) => {
    if (marcar) {
      setFieldValue(
        'tiposConta',
        TIPOS_CONTA_OPTIONS.map((o) => o.value)
      );
    } else {
      setFieldValue('tiposConta', []);
    }
  };

  const toggleTipo = (codigoTipo) => {
    if (tiposConta.includes(codigoTipo)) {
      setFieldValue(
        'tiposConta',
        tiposConta.filter((c) => c !== codigoTipo)
      );
    } else {
      setFieldValue('tiposConta', [...tiposConta, codigoTipo]);
    }
  };

  // GE / GrupoGE mutuamente exclusivos (PRD §5.3)
  const onChangeGE = (opt) => {
    const valor = opt ? opt.value : null;
    setFieldValue('codigoGE', valor);
    if (valor) setFieldValue('codigoGrupoGE', null);
  };

  const onChangeGrupoGE = (opt) => {
    const valor = opt ? opt.value : null;
    setFieldValue('codigoGrupoGE', valor);
    if (valor) setFieldValue('codigoGE', null);
  };

  const renderError = (campo) =>
    touched[campo] && errors[campo] ? (
      <div className="text-danger small mt-1">{errors[campo]}</div>
    ) : null;

  const geOptions = toGeOptions(gruposEmpresa);
  const geSelectValue =
    geOptions.find((o) => o.value === values.codigoGE) || null;

  const grupoGeOptions = toGrupoGeOptions(gruposGE);
  const grupoGeSelectValue =
    grupoGeOptions.find((o) => o.value === values.codigoGrupoGE) || null;

  const tipoAtivoSelectValue =
    TIPO_ATIVO_OPTIONS.find((o) => o.value === values.tipoAtivo) ||
    TIPO_ATIVO_OPTIONS[0];

  const agrupamentoSelectValue =
    AGRUPAMENTO_OPTIONS.find((o) => o.value === values.agrupamento) ||
    AGRUPAMENTO_OPTIONS[1];

  return (
    <Card className="mb-3 shadow-sm">
      <Card.Header style={{ backgroundColor: '#0747a6', color: '#fff' }}>
        <strong>Filtros</strong>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="small mb-1">Tipo de Ativo *</Form.Label>
              <Select
                styles={selectStylesSm}
                options={TIPO_ATIVO_OPTIONS}
                value={tipoAtivoSelectValue}
                onChange={(opt) =>
                  setFieldValue('tipoAtivo', opt ? opt.value : 'Moedas')
                }
                placeholder="Selecione..."
              />
              {renderError('tipoAtivo')}
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="small mb-1">Agrupamento</Form.Label>
              <Select
                styles={selectStylesSm}
                options={AGRUPAMENTO_OPTIONS}
                value={agrupamentoSelectValue}
                onChange={(opt) =>
                  setFieldValue(
                    'agrupamento',
                    opt ? opt.value : 'GrupoEmpresaConta'
                  )
                }
                placeholder="Selecione..."
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4} className="d-flex align-items-end">
            <Form.Group className="mb-0">
              <Form.Check
                type="checkbox"
                id="quebraPagina"
                label="Quebra página por agrupamento"
                checked={!!values.quebraPagina}
                onChange={(e) =>
                  setFieldValue('quebraPagina', e.target.checked)
                }
              />
            </Form.Group>
          </Col>
        </Row>

        <hr className="my-3" />

        <Row>
          <Col xs={12}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Form.Label className="mb-0 small">
                Tipos de Conta *
              </Form.Label>
              <Form.Check
                type="checkbox"
                id="tiposConta-todos"
                label="Todos"
                checked={todosMarcados}
                onChange={(e) => toggleTodos(e.target.checked)}
              />
            </div>
            <div className="d-flex flex-wrap" style={{ gap: '8px 16px' }}>
              {TIPOS_CONTA_OPTIONS.map((opt) => (
                <Form.Check
                  key={`tipo-${opt.value}`}
                  type="checkbox"
                  id={`tipoConta-${opt.value}`}
                  label={opt.label}
                  checked={tiposConta.includes(opt.value)}
                  onChange={() => toggleTipo(opt.value)}
                />
              ))}
            </div>
            {renderError('tiposConta')}
          </Col>
        </Row>

        <hr className="my-3" />

        <Row>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="small mb-1">
                Grupo de Empresa{' '}
                <small className="text-muted">
                  (exclusivo com Grupo de GE)
                </small>
              </Form.Label>
              <Select
                styles={selectStylesSm}
                options={geOptions}
                value={geSelectValue}
                onChange={onChangeGE}
                placeholder={
                  loadingOpcoes ? 'Carregando...' : 'Todas as GEs do usuário'
                }
                isClearable
                isDisabled={loadingOpcoes || !!values.codigoGrupoGE}
                noOptionsMessage={() => 'Nenhuma GE encontrada'}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6}>
            <Form.Group>
              <Form.Label className="small mb-1">
                Grupo de GE{' '}
                <small className="text-muted">(exclusivo com GE)</small>
              </Form.Label>
              <Select
                styles={selectStylesSm}
                options={grupoGeOptions}
                value={grupoGeSelectValue}
                onChange={onChangeGrupoGE}
                placeholder={
                  loadingOpcoes ? 'Carregando...' : 'Nenhum selecionado'
                }
                isClearable
                isDisabled={loadingOpcoes || !!values.codigoGE}
                noOptionsMessage={() => 'Nenhum grupo encontrado'}
              />
              {touched.codigoGrupoGE && errors['ge-ou-grupo-ge'] && (
                <div className="text-danger small mt-1">
                  {errors['ge-ou-grupo-ge']}
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="small mb-1">
                Dias mínimos sem conferência *
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={values.diasMinimos != null ? values.diasMinimos : ''}
                onChange={(e) => {
                  const valor = e.target.value;
                  setFieldValue(
                    'diasMinimos',
                    valor === '' ? '' : Number(valor)
                  );
                }}
                isInvalid={touched.diasMinimos && !!errors.diasMinimos}
              />
              {renderError('diasMinimos')}
            </Form.Group>
          </Col>
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="small mb-1">
                Qtde. mínima de movtos não conferidos *{' '}
                <small className="text-muted">(0 = todos)</small>
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={
                  values.qtdeMovtosMinima != null
                    ? values.qtdeMovtosMinima
                    : ''
                }
                onChange={(e) => {
                  const valor = e.target.value;
                  setFieldValue(
                    'qtdeMovtosMinima',
                    valor === '' ? '' : Number(valor)
                  );
                }}
                isInvalid={
                  touched.qtdeMovtosMinima && !!errors.qtdeMovtosMinima
                }
              />
              {renderError('qtdeMovtosMinima')}
            </Form.Group>
          </Col>
          <Col xs={12} md={3}>
            <Form.Group>
              <Form.Label className="small mb-1">
                Código da Conta Específica
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={
                  values.codigoContaEspecifica != null
                    ? values.codigoContaEspecifica
                    : ''
                }
                onChange={(e) => {
                  const valor = e.target.value;
                  setFieldValue(
                    'codigoContaEspecifica',
                    valor === '' ? null : Number(valor)
                  );
                }}
                placeholder="Opcional"
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={3} className="d-flex align-items-end">
            <Form.Group className="mb-0">
              <div className="d-flex align-items-center">
                <Form.Check
                  type="checkbox"
                  id="naoConsiderarSaldoConfRecente"
                  label="Não considera saldo conf. maior que último mov."
                  checked={!!values.naoConsiderarSaldoConfRecente}
                  onChange={(e) =>
                    setFieldValue(
                      'naoConsiderarSaldoConfRecente',
                      e.target.checked
                    )
                  }
                />
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="tooltip-checkbox14">
                      {TOOLTIP_CHECKBOX14}
                    </Tooltip>
                  }
                >
                  <FaInfoCircle
                    className="text-muted ml-1"
                    style={{ cursor: 'help' }}
                  />
                </OverlayTrigger>
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col xs={12} className="d-flex flex-wrap" style={{ gap: '8px' }}>
            <Button
              variant="primary"
              onClick={submitForm}
              disabled={loading || isSubmitting}
            >
              <FaSearch className="mr-1" /> Gerar relatório
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => {
                resetForm({ values: defaultValues });
              }}
              disabled={loading || isSubmitting}
            >
              <FaTrash className="mr-1" /> Limpar
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default CardFiltros;
